import { type GetProps, SizableText, XStack, YStack } from 'tamagui'
import { Card } from './Card'

/**
 * A titled form section card (BuyerFlo design system): elevated card, section
 * header (title + optional description), optional right-aligned action, body.
 */
export function FormSection({
    title,
    description,
    action,
    children,
}: {
    title?: string
    description?: string
    action?: React.ReactNode
    children: React.ReactNode
}) {
    return (
        <Card
            padding="$4"
            gap="$3.5"
        >
            {(title || action) && (
                <XStack
                    justifyContent="space-between"
                    alignItems="center"
                    gap="$3"
                >
                    <YStack
                        gap="$1"
                        flex={1}
                        minWidth={0}
                    >
                        {title ? (
                            <SizableText
                                fontSize={16}
                                fontWeight="600"
                                letterSpacing={-0.1}
                            >
                                {title}
                            </SizableText>
                        ) : null}
                        {description ? (
                            <SizableText
                                fontSize={13}
                                color="$colorMuted"
                            >
                                {description}
                            </SizableText>
                        ) : null}
                    </YStack>
                    {action}
                </XStack>
            )}
            {children}
        </Card>
    )
}

/**
 * A labelled field cell — uppercase label above its control, optional hint below.
 *
 * Sizes to its content by default so it stacks correctly in a vertical form. To
 * lay two fields side by side, wrap them in an `XStack` and pass `flex={1}` to
 * each — do NOT rely on a built-in flex, or stacked fields collapse vertically
 * inside flex-column containers (e.g. a Dialog) where Yoga's min-height is 0.
 * Extra layout props (flex, width, …) are forwarded to the outer stack.
 */
export function Field({
    label,
    hint,
    required,
    children,
    ...stackProps
}: {
    label?: string
    hint?: string
    required?: boolean
    children: React.ReactNode
} & GetProps<typeof YStack>) {
    return (
        <YStack
            gap="$1.5"
            minWidth={0}
            {...stackProps}
        >
            {label ? (
                <SizableText
                    fontSize={11}
                    fontWeight="500"
                    letterSpacing={0.4}
                    textTransform="uppercase"
                    color="$colorMuted"
                >
                    {label}
                    {required ? ' *' : ''}
                </SizableText>
            ) : null}
            {children}
            {hint ? (
                <SizableText
                    fontSize={12}
                    color="$colorFaint"
                >
                    {hint}
                </SizableText>
            ) : null}
        </YStack>
    )
}
